import SERVICES_API_V2 from 'api/services/v2'
import renderCheckboxGroup from 'components/FormControls/checkboxGroup'
import renderRadioGroup from 'components/FormControls/radioGroup'
import renderInputField from 'components/FormControls/textInput'
import { Typography } from 'gu-ui-kit'
import omit from 'lodash/omit'
import formStyles from 'modules/Forms/common-form.module.scss'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import validator from 'utils/asyncValidator'
import * as Yup from 'yup'
import accessesMap from 'modules/Forms/Account/AccessCreate/VkAds/accessesMap'
import oneOfMapper from 'utils/validators/oneOfMapper'
import { ACCESS_CREATE } from 'modules/Services/types'
import PageHeader from 'components/PageHeader'
import clsx from 'clsx'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import ActionButtons from 'modules/Forms/Account/AccessCreate/subcomponents/ActionButtons'

const validationSchema = Yup.object().shape({
  agency_manager: Yup.object().shape({
    username: Yup.string().required('common::validation_messages.required'),
    external_id: Yup.number().required('common::validation_messages.required'),
  }),
  agency_client: Yup.object().shape({
    external_id: Yup.number().required('common::validation_messages.required'),
  }),
  type: Yup.string().oneOf(['readonly', 'full_access'], ({ values }) =>
    oneOfMapper(
      values,
      'common::validation_messages.oneOf',
      `services::forms.account_access_create.vk_ads.fields.type.options.`,
    ),
  ),
  section: Yup.array()
    .of(
      Yup.string().oneOf(['fin', 'ads'], ({ values }) =>
        oneOfMapper(
          values,
          'common::validation_messages.oneOf',
          `services::forms.account_access_create.vk_ads.fields.section.options.`,
        ),
      ),
    )
    .min(1, 'common::validation_messages.check_required'),
  access_type: Yup.string().oneOf(
    [
      '',
      'full_access',
      'readonly',
      'fin_readonly',
      'ads_readonly',
      'vkads_full_readonly_access',
    ],
    ({ values }) => oneOfMapper(values, 'common::validation_messages.oneOf'),
  ),
})

let VkAdsAccountAccessCreateForm = ({
  handleSubmit,
  submitting,
  form,
  onClose,
  networkType,
  accountId,
  accessId,
  autofill,
  touch,
  initialValues,
}) => {
  const breakpoint = useBreakpoint()
  const { t } = useTranslation()
  const selector = formValueSelector(form)
  const {
    formValues: { type, section },
  } = useSelector((state) => ({
    formValues: selector(state, 'type', 'section'),
  }))

  useEffect(
    () =>
      SERVICES_API_V2.getNetworkAccountById({ accountId })
        .then(({ response, error }) => {
          if (error) {
            throw error
          }

          autofill(
            'agency_client.external_id',
            Number(response?.data?.external_id),
          )
        })
        .catch(onClose),
    [],
  )

  useEffect(() => {
    const { section, type } = accessesMap[initialValues.access_type][0]
    autofill('section', section)
    autofill('type', type)
  }, [initialValues])

  const submit = useCallback(
    (values, dispatch) => {
      const data = accessId
        ? { access_type: values.access_type }
        : omit(values, ['section', 'type'])

      return new Promise((resolve, reject) => {
        dispatch({
          meta: { resolve, reject },
          type: ACCESS_CREATE,
          payload: {
            networkType,
            accountId,
            accessId,
            data,
            formName: form,
          },
        })
      }).then(onClose)
    },
    [onClose, networkType, accountId, accessId],
  )

  useEffect(() => {
    if (!type || !section) {
      return
    }

    const accessTypeValue = Object.keys(accessesMap).find((accessTypeValue) =>
      accessesMap[accessTypeValue].find(
        (pair) =>
          section.every((r) => pair.section.includes(r)) && pair.type === type,
      ),
    )

    autofill('access_type', accessTypeValue)
    touch('access_type')
  }, [type, section])

  return (
    <form onSubmit={handleSubmit(submit)} noValidate>
      <PageHeader className={'margin-bottom-16'}>
        {t(`services::networks.${networkType}`)}
      </PageHeader>
      <Typography
        variant={'Body-20'}
        color={'Label-Secondary'}
        className={clsx({
          ['margin-bottom-64']: !breakpoint.md,
          ['margin-bottom-48']: breakpoint.md,
        })}
      >
        {t('services::forms.account_access_create.description')}
      </Typography>

      <div className={'margin-bottom-16'}>
        <Field
          hidden
          disabled
          fullWidth
          name="agency_client.external_id"
          component={renderInputField}
          t={t}
          label={t(
            'services::forms.account_access_create.vk_ads.fields.agency_client.external_id.label',
          )}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Field
          fullWidth
          name="agency_manager.username"
          component={renderInputField}
          t={t}
          label={t(
            'services::forms.account_access_create.vk_ads.fields.agency_manager.username.label',
          )}
          disabled={!!accessId}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Field
          fullWidth
          name="agency_manager.external_id"
          component={renderInputField}
          t={t}
          format={(value) => (value === null ? '' : '' + value)}
          parse={(value) => Number(value.replace(/\D/g, '')) || null}
          label={t(
            'services::forms.account_access_create.vk_ads.fields.agency_manager.external_id.label',
          )}
          disabled={!!accessId}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Typography variant={'p8'} className={formStyles['legend']}>
          {t('services::forms.account_access_create.vk_ads.fields.type.label')}:
        </Typography>

        <Field
          name="type"
          t={t}
          component={renderRadioGroup}
          options={['readonly', 'full_access'].map((option) => ({
            value: option,
            label: t(
              `services::forms.account_access_create.vk_ads.fields.type.options.${option}`,
            ),
          }))}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Typography variant={'p8'} className={formStyles['legend']}>
          {t(
            'services::forms.account_access_create.vk_ads.fields.section.label',
          )}
          :
        </Typography>

        <Field
          options={['fin', 'ads'].map((opt) => ({
            value: opt,
            label: t(
              `services::forms.account_access_create.vk_ads.fields.section.options.${opt}`,
            ),
          }))}
          component={renderCheckboxGroup}
          name="section"
          t={t}
        />
      </div>

      <div className={'margin-bottom-16'}>
        <Field
          hidden
          disabled
          fullWidth
          name="access_type"
          component={renderInputField}
          t={t}
          label={t(
            'services::forms.account_access_create.vk_ads.fields.access_type.label',
          )}
        />
      </div>

      <ActionButtons
        submitting={submitting}
        onClose={onClose}
        accessId={accessId}
      />
    </form>
  )
}

VkAdsAccountAccessCreateForm = reduxForm({
  form: 'VkAdsAccountAccessCreateForm',
  asyncValidate: validator(validationSchema),
  initialValues: {
    type: '',
    section: [],
    access_type: 'readonly',
    agency_client: {
      username: '',
      external_id: null,
    },
    agency_manager: {
      external_id: null,
      username: '',
    },
  },
})(VkAdsAccountAccessCreateForm)

VkAdsAccountAccessCreateForm.propTypes = {
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  autofill: PropTypes.func,
  touch: PropTypes.func,
  change: PropTypes.func,
  form: PropTypes.string,
  onClose: PropTypes.func,
  networkType: PropTypes.string,
  accountId: PropTypes.string,
  accessId: PropTypes.string,
  initialValues: PropTypes.object,
}

export default VkAdsAccountAccessCreateForm
